import { ColumnDef, Row, Table } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVerticalIcon } from "lucide-react";
import { TableCell } from "@/components/ui/table";
import { CellHoverClassName } from "@/components/List/ListTable/TableBody/Rows/TableDataRow";

type ActionsMenuColumnOptions<T> = Partial<ColumnDef<T>> & {
	actions: {
		label: string;
		destructive?: boolean;
		disabled?: boolean;
		onHandleHidden?: (row: Row<T>, table: Table<T>) => boolean;
		renderMenuItem?: (row: Row<T>, table: Table<T>) => React.ReactNode;
		onSelect?: (row: Row<T>, table: Table<T>) => void;
	}[];
	align?: "left" | "center" | "right";
	className?: string;
	meta?: Record<string, unknown>;
};

export const ActionsMenuColumn = <T,>({
	actions,
	id = "actions",
	header,
	align = "center",
	className = "",
	meta,
	...rest
}: ActionsMenuColumnOptions<T>): ColumnDef<T> => {
	const alignment = {
		left: "justify-start",
		center: "justify-center",
		right: "justify-end",
	}[align];

	return {
		id,
		header,
		enableHiding: false,
		meta: {
			wrapWithTableCell: false,
			cellClassName: className,
			...meta,
		},
		cell: ({ row, table }) => (
			<TableCell className={`${CellHoverClassName} ${className}`}>
				<div className={`flex items-center ${alignment} w-full h-full`}>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button size="icon" variant="ghost">
								<MoreVerticalIcon className="size-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="min-w-28">
							{actions.map((action, i) => {
								const handleHidden = action.onHandleHidden;
								if (handleHidden) {
									const hide = handleHidden(row, table);
									if (hide) {
										return null;
									}
								}
								return (
									<DropdownMenuItem
										key={i}
										onSelect={
											action.renderMenuItem
												? undefined
												: () => requestAnimationFrame(() => action.onSelect?.(row, table))
										}
										disabled={action.disabled}
										className={action.destructive ? "text-destructive" : ""}
									>
										{action.renderMenuItem ? action.renderMenuItem(row, table) : action.label}
									</DropdownMenuItem>
								);
							})}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</TableCell>
		),
		...rest,
	};
};
